import { getPublicSettingsBulk } from '../api/client';

interface TrackingIds {
  googleAnalyticsId: string;
  googleTagManagerId: string;
  metaPixelId: string;
  googleAdsConversionId: string;
}

let loaded = false;
let trackingIds: TrackingIds = {
  googleAnalyticsId: '',
  googleTagManagerId: '',
  metaPixelId: '',
  googleAdsConversionId: '',
};

// ─── Meta Pixel ───
function injectMetaPixel(pixelId: string): void {
  if (!pixelId || document.getElementById('meta-pixel-script')) return;

  const script = document.createElement('script');
  script.id = 'meta-pixel-script';
  script.innerHTML = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${pixelId}');
    fbq('track', 'PageView');
  `;
  document.head.appendChild(script);
}

// ─── Google Ads (gtag) ───
function injectGoogleAds(conversionId: string): void {
  if (!conversionId || document.getElementById('google-ads-script')) return;

  const script1 = document.createElement('script');
  script1.id = 'google-ads-script';
  script1.async = true;
  script1.src = `https://www.googletagmanager.com/gtag/js?id=${conversionId}`;
  document.head.appendChild(script1);

  const script2 = document.createElement('script');
  script2.id = 'google-ads-config';
  script2.innerHTML = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${conversionId}');
  `;
  document.head.appendChild(script2);
}

// ─── Google Tag Manager ───
function injectGTM(gtmId: string): void {
  if (!gtmId || document.getElementById('gtm-script')) return;

  const script = document.createElement('script');
  script.id = 'gtm-script';
  script.innerHTML = `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${gtmId}');
  `;
  document.head.appendChild(script);

  const noscript = document.createElement('noscript');
  noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}"
    height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
  document.body.insertBefore(noscript, document.body.firstChild);
}

// ─── Load tracking IDs from CMS ───
export async function initTracking(): Promise<void> {
  if (loaded) return;
  loaded = true;

  try {
    const res = await getPublicSettingsBulk([
      'google_analytics_id',
      'google_tag_manager_id',
      'meta_pixel_id',
      'google_ads_conversion_id',
    ]);
    const settings = res.data ?? {};
    trackingIds = {
      googleAnalyticsId: settings.google_analytics_id ?? '',
      googleTagManagerId: settings.google_tag_manager_id ?? '',
      metaPixelId: settings.meta_pixel_id ?? '',
      googleAdsConversionId: settings.google_ads_conversion_id ?? '',
    };

    injectMetaPixel(trackingIds.metaPixelId);
    injectGoogleAds(trackingIds.googleAdsConversionId);
    injectGTM(trackingIds.googleTagManagerId);
  } catch {
    // Silently fail — tracking should never break the app
  }
}

// ─── Meta Events ───
export function trackMetaEvent(
  eventName: string,
  params?: Record<string, any>
): void {
  if (!trackingIds.metaPixelId || !(window as any).fbq) return;
  (window as any).fbq('track', eventName, params);
}

// ─── Google Ads Events ───
export function trackGoogleEvent(
  eventName: string,
  params?: Record<string, any>
): void {
  if (!trackingIds.googleAdsConversionId || !(window as any).gtag) return;
  (window as any).gtag('event', eventName, params);
}

// ─── GTM / dataLayer ───
export function trackGTMEvent(eventName: string, data: Record<string, any>): void {
  if (!(window as any).dataLayer) return;
  (window as any).dataLayer.push({
    event: eventName,
    ...data,
  });
}

// ─── Route Change Tracking ───
export function trackPageView(path?: string): void {
  const pagePath = path ?? window.location.pathname + window.location.search;

  // Meta Pixel
  if (trackingIds.metaPixelId && (window as any).fbq) {
    (window as any).fbq('track', 'PageView');
  }

  // Google Ads
  if (trackingIds.googleAdsConversionId && (window as any).gtag) {
    (window as any).gtag('config', trackingIds.googleAdsConversionId, {
      page_path: pagePath,
    });
  }

  // GTM
  if ((window as any).dataLayer) {
    (window as any).dataLayer.push({
      event: 'pageview',
      pagePath,
    });
  }
}

// ─── E-commerce Events ───
export function trackViewContent(
  contentName: string,
  contentIds: string[],
  value: number,
  currency: string = 'INR'
): void {
  trackMetaEvent('ViewContent', {
    content_name: contentName,
    content_ids: contentIds,
    value,
    currency,
  });
  trackGoogleEvent('view_item', {
    items: contentIds.map((id) => ({ item_id: id, item_name: contentName })),
    value,
    currency,
  });
}

export function trackLead(contentName?: string, value?: number): void {
  trackMetaEvent('Lead', {
    content_name: contentName ?? 'Website Lead',
    value: value ?? 0,
  });
  trackGoogleEvent('generate_lead', {
    currency: 'INR',
    value: value ?? 0,
  });
}

export function trackInitiateCheckout(
  contentName: string,
  value: number,
  currency: string = 'INR'
): void {
  trackMetaEvent('InitiateCheckout', {
    content_name: contentName,
    value,
    currency,
  });
  trackGoogleEvent('begin_checkout', {
    value,
    currency,
  });
}

export function trackPurchase(
  contentName: string,
  value: number,
  orderId: string,
  currency: string = 'INR'
): void {
  trackMetaEvent('Purchase', {
    content_name: contentName,
    value,
    currency,
    order_id: orderId,
  });
  trackGoogleEvent('purchase', {
    transaction_id: orderId,
    value,
    currency,
    items: [{ item_name: contentName }],
  });
  trackGTMEvent('ecm_purchase', {
    ecommerce: {
      transaction_id: orderId,
      value,
      currency,
      items: [{ item_name: contentName, price: value }],
    },
  });
}

export function trackCompleteRegistration(contentName?: string): void {
  trackMetaEvent('CompleteRegistration', {
    content_name: contentName ?? 'User Registration',
    status: 'success',
  });
  trackGoogleEvent('sign_up', {});
}

export function trackContact(method: string = 'WhatsApp'): void {
  trackMetaEvent('Contact', { method });
}

// ─── WhatsApp Click Tracking ───
export function trackWhatsAppClick(ref: string): void {
  trackMetaEvent('Contact', { method: 'WhatsApp', ref });
  trackGoogleEvent('conversion', {
    send_to: trackingIds.googleAdsConversionId,
  });
}
